import React, { useState, useEffect } from "react";
import {
  OMNISCAN_CORPORATE_RED,
  OMNISCAN_CORPORATE_GREEN,
  OMNISCAN_CORPORATE_TURQUOIS,
  OMNISCAN_CORPORATE_PINK,
  OMNISCAN_CORPORATE_PURPLE
} from "../../../constants/colors";


import AssetChartOutages from "./AssetChartOutages";
import AssetChartAvailability from "./AssetChartAvailability";

/*
  AssetDetailDrawerContent
  - UI-only content for the WHITE detail drawer
*/



/* ======================================================
   MAIN COMPONENT
====================================================== */

export default function AssetDetailDrawerContent(props) {

    const title = props.title || "Filtered Asset Detail";
    const subtitle = props.subtitle || "High / Fair (example)";

    /* =========================
       COLOURS
    ========================== */

    function adjustColor(hex, amount) {
        let color = hex.replace("#", "");

        let r = parseInt(color.substring(0, 2), 16);
        let g = parseInt(color.substring(2, 4), 16);
        let b = parseInt(color.substring(4, 6), 16);

        r = Math.min(255, Math.max(0, r + amount));
        g = Math.min(255, Math.max(0, g + amount));
        b = Math.min(255, Math.max(0, b + amount));

        return (
            "#" +
            r.toString(16).padStart(2, "0") +
            g.toString(16).padStart(2, "0") +
            b.toString(16).padStart(2, "0")
        );
    }

    const OMNI = {
        TURQUOIS: OMNISCAN_CORPORATE_TURQUOIS,
        GREEN: OMNISCAN_CORPORATE_GREEN,
        RED: OMNISCAN_CORPORATE_RED,
        PINK: OMNISCAN_CORPORATE_PINK,
        PURPLE: OMNISCAN_CORPORATE_PURPLE
    };

    /* =========================
       MEMO DATA
    ========================== */


	const assets = React.useMemo(function () {
	    return props.assets || [
	        {
	            code: "AHU-102",
	            name: "Lighting Panel",
	            condition: "Fair",
	            criticality: "High",
	            lastSeen: "2026-02-12",
	            downtime: "12h",
	            image: "/assetimages/lighting_panel.jpg"
	        },
	        {
	            code: "LGT-220",
	            name: "Monitor",
	            condition: "Poor",
	            criticality: "Medium",
	            lastSeen: "2026-02-15",
	            downtime: "4h",
	            image: "/assetimages/monitor.png"
	        },
	        {
	            code: "FIR-019",
	            name: "Assembly Robot",
	            condition: "Good",
	            criticality: "High",
	            lastSeen: "2026-02-20",
	            downtime: "0h",
				image: "/assetimages/robot.jpg"
	        },
	        {
	            code: "IT-778",
	            name: "Desk A1",
	            condition: "Fair",
	            criticality: "Low",
	            lastSeen: "2026-02-18",
	            downtime: "1h",
				image: "/assetimages/desk.jpg"
	        }
	    ];
	}, [props.assets]);

    /* =========================
       STATE
    ========================== */

    const [search, setSearch] = useState("");
    const [filteredAssets, setFilteredAssets] = useState([]);

    useEffect(function () {

        const s = (search || "").toLowerCase().trim();

        if (!s) {
            setFilteredAssets(assets);
            return;
        }

        setFilteredAssets(
            assets.filter(function (a) {
                return (
                    (a.code || "").toLowerCase().includes(s) ||
                    (a.name || "").toLowerCase().includes(s) ||
                    (a.condition || "").toLowerCase().includes(s) ||
                    (a.criticality || "").toLowerCase().includes(s)
                );
            })
        );

    }, [search, assets]);
	
	const openAsset = (id) => {
			if(props.setAssetOpen){
				props.setAssetOpen(true);
			}
		}

    

    /* =========================
       KPI DATA
    ========================== */

    const kpis = props.kpis || {
        assets: 128,
        uptimePercent: 97.4,
        downtimeHours: 1120,
        outages: 14
    };

    function formatPercent(v) {
        return v === null || v === undefined ? "-" : v.toFixed(1) + "%";
    }

    /* =========================
       STYLES
    ========================== */

    const container = {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "18px 18px 18px 22px",
        boxSizing: "border-box",
        width: "calc(100% - 30px)"
    };

    const headerBlock = { display: "flex", flexDirection: "column", gap: "4px" };
    const hTitle = { fontSize: "18px", fontWeight: 800, color: "#111" };
    const hSub = { fontSize: "13px", color: "#666" };
    const divider = { height: "1px", background: "rgba(0,0,0,0.08)" };

    const kpiRow = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" };
    const kpiCardBase = {
        borderRadius: "14px",
        padding: "10px 14px",
        minHeight: "60px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        color: "#fff",
        boxShadow: "0 10px 22px rgba(0,0,0,0.14)"
    };

    const kpiColours = [
        "linear-gradient(135deg," + adjustColor(OMNI.TURQUOIS, -20) + "," + adjustColor(OMNI.TURQUOIS, 25) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.GREEN, -20) + "," + adjustColor(OMNI.GREEN, 25) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.PURPLE, -25) + "," + adjustColor(OMNI.PURPLE, 30) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.PINK, -20) + "," + adjustColor(OMNI.PINK, 25) + ")"
    ];

    const kpiLabel = { fontSize: "10px", letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.9 };
    const kpiValue = { fontSize: "20px", fontWeight: 800 };
    const kpiSub = { fontSize: "11px", opacity: 0.9 };

    const gridTwoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };
    const sectionCard = { borderRadius: "16px", background: "#fff", boxShadow: "0 14px 28px rgba(0,0,0,0.10)", padding: "14px" };
    const sectionTitle = { fontSize: "11px", letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 800, color: "#444", marginBottom: "8px" };
    const placeholderChart = { height: "150px", borderRadius: "14px", background: "linear-gradient(180deg,#f5f6f8,#eceef2)", border: "1px solid rgba(0,0,0,0.06)" };

    const rightPanel = { borderRadius: "16px", background: "rgba(245,246,248,0.95)", border: "1px solid rgba(0,0,0,0.06)", padding: "12px", minHeight: "330px" };
    const searchBox = { height: "34px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.15)", padding: "0 12px", fontSize: "12px" };
    const list = { display: "flex", flexDirection: "column", gap: "8px", overflowY: "auto" };
    const assetTop = { display: "flex", justifyContent: "space-between", marginBottom: "4px" };
    const assetCode = { fontSize: "12px", fontWeight: 800 };
    const assetName = { fontSize: "13px", fontWeight: 600 };
    const assetMeta = { fontSize: "12px", color: "#666" };
    const smallPill = { padding: "3px 10px", borderRadius: "14px", background: "rgba(0,0,0,0.06)", fontSize: "11px" };

   
	const assetRow = {
	    display: "flex",
	    gap: "16px",
	    padding: "14px 18px",
	    borderRadius: "12px",
	    background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
	    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
	    marginBottom: "10px",
	    cursor: "pointer",
	    transition: "all 0.18s ease",
	    position: "relative"
	};
	
	
	const assetRowHover = {
	    transform: "translateY(-1px)",
	    boxShadow: "0 6px 18px rgba(0,0,0,0.12)"
	};
	
	
	const pillBase = {
	    minWidth: "110px",
	    height: "26px",
	    borderRadius: "14px",
	    fontSize: "12px",
	    fontWeight: 600,
	    color: "#fff",
	    display: "flex",
	    alignItems: "center",
	    justifyContent: "center",
	    letterSpacing: "0.3px",
	    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
	};

	const assetImageWrap = {
	    width: "54px",
	    height: "54px",
	    flexShrink: 0,
	    borderRadius: "50%",
	    overflow: "hidden",
	    background: "#222",
	    boxShadow: "0 4px 10px rgba(0,0,0,0.35)"
	};

	const assetImage = {
	    width: "100%",
	    height: "100%",
	    objectFit: "cover"
	};

	const assetContent = {
	    flex: 1,
	    display: "flex",
	    flexDirection: "column",
	    gap: "4px"
	};
	
	
	
	const joinedPill = {
	    display: "flex",
	    alignItems: "center",
	    overflow: "hidden",
	    borderRadius: "16px",
	    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
	};
	
	
	function getConditionSegment(condition) {
	    var base = {
	        minWidth: "90px",
	        height: "20px",
	        display: "flex",
	        alignItems: "center",
	        justifyContent: "center",
	        fontSize: "12px",
	        fontWeight: 600,
	        color: "#fff",
	        borderTopLeftRadius: "16px",
	        borderBottomLeftRadius: "16px"
	    };

	    if (condition === "Good") {
	        base.background = "linear-gradient(180deg, #1db954, #08933a)";
	    } else if (condition === "Fair") {
	        base.background = "linear-gradient(180deg, #f5a623, #d08602)";
	    } else {
	        base.background = "linear-gradient(180deg, #d64545, #c82222)";
	    }

	    return base;
	}
	
	
	
	function getCriticalitySegment(level) {
	    var base = {
	        minWidth: "90px",
	        height: "20px",
	        display: "flex",
	        alignItems: "center",
	        justifyContent: "center",
	        fontSize: "12px",
	        fontWeight: 600,
	        color: "#fff",
	        borderTopRightRadius: "16px",
	        borderBottomRightRadius: "16px"
	    };

	    if (level === "High") {
	        base.background = "linear-gradient(180deg, #c0392b, #992216)";
	    } else if (level === "Medium") {
	        base.background = "linear-gradient(180deg, #f39c12, #c67900)";
	    } else {
	        base.background = "linear-gradient(180deg, #7f8c8d, #606b6c)";
	    }

	    return base;
	}
	
	 /* =========================
       RENDER
    ========================== */

    return (
        <div style={container}>

            <div style={headerBlock}>
                <div style={hTitle}>{title}</div>
                <div style={hSub}>{subtitle}</div>
            </div>

            <div style={divider} />

            <div style={kpiRow}>
                {[
                    { label: "Assets", value: kpis.assets, sub: "in selection" },
                    { label: "Uptime", value: formatPercent(kpis.uptimePercent), sub: "30 days" },
                    { label: "Downtime", value: kpis.downtimeHours, sub: "hours" },
                    { label: "Outages", value: kpis.outages, sub: "events" }
                ].map(function (k, i) {
                    return (
                        <div key={k.label} style={{ ...kpiCardBase, background: kpiColours[i] }}>
                            <div style={kpiLabel}>{k.label}</div>
                            <div style={kpiValue}>{k.value}</div>
                            <div style={kpiSub}>{k.sub}</div>
                        </div>
                    );
                })}
            </div>

            <div style={gridTwoCol}>

                <div style={sectionCard}>
                    <div style={sectionTitle}>Outages</div>
                    <AssetChartOutages/>

                    <div style={{ marginTop: "12px" }}>
                        <div style={sectionTitle}>Timeline</div>
                       	<AssetChartAvailability/>
                    </div>
                </div>

                <div style={rightPanel}>
                    <div style={sectionTitle}>Assets</div>

                    <input
                        style={searchBox}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search assets"
                    />
					<div style={{height:'20px'}}/>
					<div style={list}>
					    {filteredAssets.map(function (a) {
					        return (
					            <div key={a.code} style={assetRow}>
					                
					                {/* CIRCULAR PHOTO */}
					                <div style={assetImageWrap} onClick={() => {openAsset(-100)}}>
					                    <img
					                        src={a.image || "/assets/images/lighting-panel.jpg"}
					                        alt={a.name}
					                        style={assetImage}
					                    />
					                </div>

					                {/* CONTENT */}
					                <div style={assetContent}>
									<div style={assetTop}>
									    <div style={assetCode}>{a.code}</div>

										<div style={joinedPill}>
										    <div style={getConditionSegment(a.condition)}>
										        {a.condition}
										    </div>
										    <div style={getCriticalitySegment(a.criticality)}>
										        {a.criticality}
										    </div>
										</div>
									</div>

					                    <div style={assetName}>{a.name}</div>

					                    <div style={assetMeta}>
					                        Last: {a.lastSeen} · Downtime: {a.downtime}
					                    </div>
					                </div>

					            </div>
					        );
					    })}
					</div>
                </div>

            </div>

        </div>
    );
}